// Shared succession stage-state logic used by the dashboard card and the
// Succession Overview page so the two views cannot drift.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type StageKey = "profile" | "path" | "decisions" | "documents" | "execution" | "rhythm";

// Warm human labels for every raw status a member could see.
// Raw enum values must never render in member-facing UI.
export const SUCCESSION_STATUS_LABEL: Record<string, string> = {
  draft: "In progress",
  active: "In progress",
  complete: "Complete",
  path_complete: "Path complete",
  commissioned: "Serving",
  invited: "Invitation sent",
  paused_litigation: "Paused for review",
  pending: "In progress",
};

export function humanSuccessionStatus(status: string | null | undefined): string {
  if (!status) return "Not started yet";
  return SUCCESSION_STATUS_LABEL[status] ?? "In progress";
}

// Plain-voice labels for association_offices.status so raw enum
// values never render in member-facing UI. Kept next to the succession
// status map so all governance-status labels live in one place.
export const OFFICE_STATUS_LABEL: Record<string, string> = {
  proposed: "Proposed",
  active: "Adopted",
  vacant: "Open seat",
  retired: "Retired",
};

export function humanOfficeStatus(status: string | null | undefined): string {
  if (!status) return "Not set";
  return OFFICE_STATUS_LABEL[status] ?? status;
}

export type StageState = {
  loading: boolean;
  unaId: string | null;
  unaName: string;
  profileId: string | null;
  profileStatus: string | null;
  isPaused: boolean;
  successorStatus: string | null;
  decisions: any;
  branchFlags: any;
  docRows: Array<{ doc_type: string; status: string; filed_at: string | null }>;
  applicableDocTypes: string[];
  drillCount: number;
  lastDrillDate: string | null;
  stage1Done: boolean;
  stage2Unlocked: boolean;
  stage2Done: boolean;
  stage3Unlocked: boolean;
  stage3Done: boolean;
  stage4Unlocked: boolean;
  stage4Done: boolean;
  stage5Unlocked: boolean;
  stage5Done: boolean;
  stage6Unlocked: boolean;
  stage6Done: boolean;
  completedCount: number;
};

export async function resolveAdminUna(userId: string): Promise<{ id: string; name: string } | null> {
  const { data: unaAsTS } = await supabase
    .from("unas").select("id, name").eq("treasury_secretary_id", userId).maybeSingle();
  if (unaAsTS) return { id: unaAsTS.id as string, name: (unaAsTS.name as string) || "" };
  const { data: member } = await supabase
    .from("una_members")
    .select("una_id, una:una_id(name)")
    .eq("user_id", userId).eq("status", "active")
    .in("member_role", ["managing_member", "treasury_secretary"])
    .limit(1).maybeSingle();
  if (!member?.una_id) return null;
  return { id: member.una_id as string, name: (member.una as any)?.name || "" };
}

export function buildApplicableDocTypes(decisions: any, branchFlags: any): string[] {
  const base = [
    "continuity_resolution",
    "standing_successor_resolution",
    "incapacity_protocol",
    "founders_letter",
    "successors_manual",
  ];
  const businessOpted =
    branchFlags?.has_operating_business &&
    decisions?.asset_rules?.business?.prepare_acceptance !== false;
  if (businessOpted) base.push("successor_manager_acceptance");
  if (decisions?.minor_plan) base.push("readiness_transition_resolution");
  return base;
}

export function useSuccessionStageState(userId: string | undefined): StageState {
  const [state, setState] = useState<StageState>({
    loading: true, unaId: null, unaName: "", profileId: null, profileStatus: null,
    isPaused: false, successorStatus: null, decisions: null, branchFlags: null,
    docRows: [], applicableDocTypes: [], drillCount: 0, lastDrillDate: null,
    stage1Done: false, stage2Unlocked: false, stage2Done: false,
    stage3Unlocked: false, stage3Done: false,
    stage4Unlocked: false, stage4Done: false,
    stage5Unlocked: false, stage5Done: false,
    stage6Unlocked: false, stage6Done: false,
    completedCount: 0,
  });

  useEffect(() => {
    (async () => {
      if (!userId) { setState((s) => ({ ...s, loading: false })); return; }
      const una = await resolveAdminUna(userId);
      if (!una) { setState((s) => ({ ...s, loading: false })); return; }

      const { data: prof } = await supabase
        .from("succession_profiles")
        .select("id, status, decisions, branch_flags")
        .eq("association_id", una.id)
        .order("created_at", { ascending: false }).limit(1).maybeSingle();

      const profileId = prof?.id ?? null;
      const decisions = (prof as any)?.decisions || null;
      const branchFlags = (prof as any)?.branch_flags || null;

      let successorStatus: string | null = null;
      let docRows: Array<{ doc_type: string; status: string; filed_at: string | null }> = [];
      let drillCount = 0;
      let lastDrillDate: string | null = null;
      if (profileId) {
        const { data: succ } = await supabase
          .from("succession_successors")
          .select("status")
          .eq("profile_id", profileId)
          .order("created_at", { ascending: false })
          .limit(1).maybeSingle();
        successorStatus = (succ as any)?.status ?? null;

        const { data: rows } = await supabase
          .from("succession_documents")
          .select("doc_type, status, filed_at")
          .eq("profile_id", profileId);
        docRows = (rows as any) || [];

        const { data: drills } = await supabase
          .from("succession_drills")
          .select("drill_date")
          .eq("profile_id", profileId)
          .order("drill_date", { ascending: false });
        drillCount = drills?.length ?? 0;
        lastDrillDate = drills?.[0]?.drill_date ?? null;
      }

      const profileStatus = prof?.status || null;
      const isPaused = profileStatus === "paused_litigation";
      const stage1Done = profileStatus === "active" || profileStatus === "complete";
      const stage2Unlocked = stage1Done && (
        successorStatus === "active" || successorStatus === "path_complete" || successorStatus === "commissioned"
      );
      const stage2Done = successorStatus === "path_complete" || successorStatus === "commissioned";
      const stage3Unlocked = stage2Done || stage1Done;
      const stage3Done = !!decisions?._complete;

      const applicableDocTypes = buildApplicableDocTypes(decisions, branchFlags);
      const stage4Unlocked = stage3Done && !isPaused;
      const continuityRow = docRows.find((r) => r.doc_type === "continuity_resolution");
      const stage4Done =
        stage4Unlocked &&
        applicableDocTypes.every((t) => docRows.some((r) => r.doc_type === t)) &&
        continuityRow?.status === "signed_filed";

      const stage5Unlocked = !!stage4Done && !isPaused;
      const stage5Done = !!decisions?.execution_complete;

      const stage6Unlocked = stage5Done && !isPaused;
      const stage6Done = stage6Unlocked && drillCount > 0;

      const completedCount =
        (stage1Done ? 1 : 0) + (stage2Done ? 1 : 0) + (stage3Done ? 1 : 0) +
        (stage4Done ? 1 : 0) + (stage5Done ? 1 : 0) + (stage6Done ? 1 : 0);

      setState({
        loading: false, unaId: una.id, unaName: una.name, profileId, profileStatus,
        isPaused, successorStatus, decisions, branchFlags, docRows,
        applicableDocTypes, drillCount, lastDrillDate,
        stage1Done, stage2Unlocked, stage2Done, stage3Unlocked, stage3Done,
        stage4Unlocked, stage4Done: !!stage4Done,
        stage5Unlocked, stage5Done, stage6Unlocked, stage6Done, completedCount,
      });
    })();
  }, [userId]);

  return state;
}

export const CREDENTIAL_MAP_CATEGORIES = [
  "Financial institutions",
  "Portal and platforms",
  "Email and domains",
  "Signing and automation",
  "Hardware wallets and seed storage",
  "Physical keys and safes",
  "Professionals",
  "Original instruments",
] as const;

// Warm blocking guard for the credential map.
const ELEVEN_LOWER_WORDS = /(?:\b[a-z]+\b\s+){10}\b[a-z]+\b/;
const NINE_DIGITS = /\d{9,}/;
const FORBIDDEN = /(password|passphrase|pin\s|seed\s*phrase|private\s*key)/i;

export function credentialSecretGuard(text: string): string | null {
  if (!text) return null;
  if (ELEVEN_LOWER_WORDS.test(text)) {
    return "This looks like it could be a secret. The map holds locations and rules, never the secrets themselves.";
  }
  if (NINE_DIGITS.test(text)) {
    return "This looks like it could be a secret. The map holds locations and rules, never the secrets themselves.";
  }
  if (FORBIDDEN.test(text)) {
    return "This looks like it could be a secret. The map holds locations and rules, never the secrets themselves.";
  }
  return null;
}
